import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';

/**
 * AddServiceModal
 *
 * Modal for creating and editing Inpatient Services with Room Type, Service Type,
 * Unit Charge, Status, and Charge Editable flags.
 */
const AddServiceModal = ({
  isOpen,
  onClose,
  onSaveService,
  serviceToEdit,
  configuredRoomTypes = []
}) => {
  const [roomType, setRoomType] = useState('ALL');
  const [serviceType, setServiceType] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  const [unitCharge, setUnitCharge] = useState('');
  const [status, setStatus] = useState('Active');
  const [chargeEditable, setChargeEditable] = useState(true);

  const [backendRoomTypes, setBackendRoomTypes] = useState([]);

  useEffect(() => {
    if (isOpen) {
      axios.get(ENDPOINTS.GET_ALL_ROOM_TYPES)
        .then((res) => {
          if (Array.isArray(res.data)) {
            setBackendRoomTypes(res.data);
          }
        })
        .catch(() => {});
    }
  }, [isOpen]);

  // Derived room types list from backend
  const availableRoomTypes = Array.from(
    new Set([
      'ALL',
      ...(backendRoomTypes || []).map((rt) => rt.name).filter(Boolean),
      ...(configuredRoomTypes || [])
        .map((rt) => (typeof rt === 'string' ? rt : rt.name || rt.roomType))
        .filter(Boolean)
    ])
  );

  useEffect(() => {
    if (serviceToEdit) {
      setRoomType(serviceToEdit.roomType || 'ALL');
      setServiceType(serviceToEdit.serviceType || serviceToEdit.name || '');
      setServiceCode(serviceToEdit.serviceCode || serviceToEdit.code || '');
      setUnitCharge(serviceToEdit.unitCharge !== undefined ? serviceToEdit.unitCharge : serviceToEdit.price || '');
      setStatus(serviceToEdit.status || 'Active');
      setChargeEditable(serviceToEdit.chargeEditable !== undefined ? serviceToEdit.chargeEditable : true);
    } else {
      setRoomType('ALL');
      setServiceType('');
      setServiceCode(`SRV-${Math.floor(1000 + Math.random() * 9000)}`);
      setUnitCharge('');
      setStatus('Active');
      setChargeEditable(true);
    }
  }, [serviceToEdit, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!serviceType.trim()) {
      toast.error('Please enter a service type/name');
      return;
    }
    if (!unitCharge || isNaN(unitCharge) || Number(unitCharge) < 0) {
      toast.error('Please enter a valid unit charge');
      return;
    }

    const payload = {
      id: serviceToEdit ? serviceToEdit.id : null,
      roomType: roomType || 'ALL',
      serviceType: serviceType.trim(),
      serviceCode: serviceCode.trim() || `SRV-${Math.floor(1000 + Math.random() * 9000)}`,
      unitCharge: Number(unitCharge),
      status: status,
      chargeEditable: Boolean(chargeEditable)
    };

    onSaveService(payload);
    onClose();
  };

  return (
    <div className="manage-modal-backdrop" onClick={onClose}>
      <div className="manage-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="manage-modal-header">
          <h3>🩺 {serviceToEdit ? 'Edit Inpatient Service' : 'Add New Inpatient Service'}</h3>
          <button className="manage-modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="manage-modal-body">
            {/* Room Type */}
            <div className="modal-form-group">
              <label>* Applicable Room Type</label>
              <select value={roomType} onChange={(e) => setRoomType(e.target.value)}>
                {availableRoomTypes.map((rt) => {
                  const backendObj = backendRoomTypes.find((t) => t.name?.toLowerCase() === rt.toLowerCase());
                  const isInactive = backendObj && backendObj.status === 'Inactive';
                  return (
                    <option
                      key={rt}
                      value={rt}
                      style={{ color: isInactive ? '#dc2626' : 'inherit' }}
                    >
                      {rt === 'ALL' ? '🏢 All Room Types (Hospital Wide)' : rt} {isInactive ? '(Inactive)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* Service Type / Name */}
            <div className="modal-form-group">
              <label>* Service Type / Name</label>
              <input
                type="text"
                placeholder="e.g. Nursing Care, Oxygen Support, Physiotherapy"
                value={serviceType}
                onChange={(e) => setServiceType(e.target.value)}
                required
                autoFocus
              />
            </div>

            {/* Service Code */}
            <div className="modal-form-group">
              <label>Service Code / ID</label>
              <input
                type="text"
                placeholder="e.g. SRV-101"
                value={serviceCode}
                onChange={(e) => setServiceCode(e.target.value)}
              />
            </div>

            {/* Unit Charge */}
            <div className="modal-form-group">
              <label>* Unit Charge (₹)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 500"
                value={unitCharge}
                onChange={(e) => setUnitCharge(e.target.value)}
                required
              />
            </div>

            {/* Status */}
            <div className="modal-form-group">
              <label>* Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            {/* Charge Editable Flag */}
            <div className="modal-form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px' }}>
              <input
                type="checkbox"
                id="modalChargeEditableCheckbox"
                checked={chargeEditable}
                onChange={(e) => setChargeEditable(e.target.checked)}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <label htmlFor="modalChargeEditableCheckbox" style={{ margin: 0, cursor: 'pointer', fontWeight: 600 }}>
                Charge Editable at Billing / Invoicing
              </label>
            </div>
          </div>

          <div className="manage-modal-footer">
            <button type="button" className="modal-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="modal-btn-save">
              {serviceToEdit ? 'Update Service' : 'Save Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddServiceModal;
